import React, { MouseEventHandler, useState } from "react";
import {
  ConsentListStyle,
  TermOfUseModalStyle,
  ConsentActionsStyle,
  ModalContentSkeletonWrap,
  ConsentFormBodyStyle,
  CardLayoutStyle,
} from "./styles";
import ConsentItem from "./ConsentItem";
import Button from "components/foundation/Button";
import Modal from "components/foundation/Modal/Modal";
import { useForm } from "react-hook-form";
import { useSignUpTerms } from "features/auth/hooks/queries";
import Skeleton from "components/foundation/Skeleton";
import SelectButton from "components/foundation/SelectButton";
import { CheckIcon } from "assets/icons";

interface ConsentFormProps {
  onClickNext: () => void;
}

const FIELD_NAME = {
  ALL: "all",
  TERM_OF_USE: "termOfUse",
  USE_OF_PERSONAL_INFO: "useOfPersonalInfo",
};

const ConsentForm = ({ onClickNext }: ConsentFormProps) => {
  // 약관 데이터
  const { data: termOfUseData } = useSignUpTerms({
    policyType: "TERMS_OF_SERVICE",
  });
  const { data: useOfPersonalInfoData } = useSignUpTerms({
    policyType: "PRIVACY_POLICY",
  });

  // 모달 상태
  const [openTermOfUseModal, setOpenTermOfUseModal] = useState(false);
  const [openUseOfPersonalInfoModal, setOpenUseOfPersonalInfoModal] =
    useState(false);

  // 정책 및 약관 동의 폼
  const { setValue, getValues, watch } = useForm();
  const isAllChecked = watch(FIELD_NAME.ALL);
  const isTermOfUseChecked = watch(FIELD_NAME.TERM_OF_USE);
  const isUseOfPersonalInfoChecked = watch(FIELD_NAME.USE_OF_PERSONAL_INFO);

  /**
   * @description 이용약관 버튼 클릭 이벤트 핸들러
   */
  const handleClickTermOfUse = () => {
    setOpenTermOfUseModal(true);
  };

  /**
   * @description 개인정보 수집 및 이용동의 버튼 클릭 이벤트 핸들러
   */
  const handleClickUseOfPersonalInfo = () => {
    setOpenUseOfPersonalInfoModal(true);
  };

  const allChecked = watch(FIELD_NAME.ALL);

  /**
   * @description 이용약관 전체 동의 체크박스 Change 이벤트 핸들러
   */
  const handleChangeAllCheckbox: MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.preventDefault();

    setValue(FIELD_NAME.TERM_OF_USE, !allChecked);
    setValue(FIELD_NAME.USE_OF_PERSONAL_INFO, !allChecked);
    setValue(FIELD_NAME.ALL, !allChecked);
  };

  /**
   * @description 이용약관 전체 동의 체크박스 Change 이벤트 핸들러
   */
  const handleChangeConsentItem = (fieldName: string) => (checked: boolean) => {
    setValue(fieldName, checked);

    const requiredCheckList = getValues([
      FIELD_NAME.TERM_OF_USE,
      FIELD_NAME.USE_OF_PERSONAL_INFO,
    ]);

    setValue(
      FIELD_NAME.ALL,
      requiredCheckList.every((item) => Boolean(item)),
    );
  };

  return (
    <>
      <CardLayoutStyle
        header={{
          title: "정책 및 약관 동의",
          description: "이용약관 및 개인정보 처리방침에 동의해 주세요.",
        }}
        gap={30}
        bodyStyle={{
          justifyContent: "space-between",
        }}
      >
        <ConsentFormBodyStyle>
          <SelectButton
            selected={allChecked}
            rightIcon={<CheckIcon />}
            onClick={handleChangeAllCheckbox}
          >
            이용약관 전체 동의
          </SelectButton>

          <ConsentListStyle>
            <ConsentItem
              label="이용약관 (필수)"
              checked={isTermOfUseChecked}
              onCheckedChange={handleChangeConsentItem(FIELD_NAME.TERM_OF_USE)}
              onClickOpenModal={handleClickTermOfUse}
            />
            <ConsentItem
              label="개인정보 수집 및 이용동의 (필수)"
              checked={isUseOfPersonalInfoChecked}
              onCheckedChange={handleChangeConsentItem(
                FIELD_NAME.USE_OF_PERSONAL_INFO,
              )}
              onClickOpenModal={handleClickUseOfPersonalInfo}
            />
          </ConsentListStyle>
        </ConsentFormBodyStyle>

        <ConsentActionsStyle>
          <Button
            size="large"
            disabled={!isAllChecked}
            onClick={onClickNext}
            fill
          >
            다음
          </Button>
        </ConsentActionsStyle>
      </CardLayoutStyle>

      <Modal.Root
        open={openTermOfUseModal}
        onOpenChange={setOpenTermOfUseModal}
      >
        <TermOfUseModalStyle title="이용약관" confirm>
          {termOfUseData?.policyText ?? <ModalContentSkeleton />}
        </TermOfUseModalStyle>
      </Modal.Root>

      <Modal.Root
        open={openUseOfPersonalInfoModal}
        onOpenChange={setOpenUseOfPersonalInfoModal}
      >
        <TermOfUseModalStyle title="개인정보 수집 및 이용동의 (필수)" confirm>
          {useOfPersonalInfoData?.policyText ?? <ModalContentSkeleton />}
        </TermOfUseModalStyle>
      </Modal.Root>
    </>
  );
};

const ModalContentSkeleton = () => {
  return (
    <ModalContentSkeletonWrap>
      <Skeleton type="text" />
      {Array.from({ length: 20 }).map((_, index) => (
        <Skeleton key={index} type="text" />
      ))}
    </ModalContentSkeletonWrap>
  );
};

export default ConsentForm;
